import type { Interval, StopGeoJson, VehicleTrackingGeoJson } from '@/types';
import { type Dayjs } from 'dayjs';
import type { FeatureCollection } from 'geojson';

export type PickUpMapTripServiceStep = 'waitingOnOtherStops' | 'pickUpOnNextStop' | 'earlyForPickUp' | 'pickingUp';

export type DropOffMapTripServiceStep = 'waitingOnOtherStops' | 'dropOffOnNextStop' | 'droppingOff' | 'completed';

export type TrackingErrorTripServiceStep = 'beforePickUp' | 'afterPickUp';

export type TripServiceStop = 'origin' | 'destination';

export type TripServiceEta<Stop extends TripServiceStop = TripServiceStop> = {
  stop: Stop;
  time: Dayjs;
  isStale: boolean;
  isNext: boolean;
};

export type TripServiceEtas = {
  origin: TripServiceEta<'origin'>;
  destination: TripServiceEta<'destination'>;
};

export class VehicleTracking {
  readonly geoJson: VehicleTrackingGeoJson;

  constructor(geoJson: VehicleTrackingGeoJson) {
    this.geoJson = geoJson;
  }

  get type(): 'accessibleMiniVan' | 'fullSizeVan' | 'taxi' {
    return this.geoJson.features[0].properties.type;
  }

  get currentStreet(): string {
    return this.geoJson.features[0].properties.currentStreet;
  }

  get nearStreetIntersection(): string | null {
    return this.geoJson.features[0].properties.nearStreetIntersection;
  }
}

export class Stop {
  readonly geoJson: StopGeoJson;

  constructor(geoJson: StopGeoJson) {
    this.geoJson = geoJson;
  }

  get address(): string {
    return this.geoJson.features[0].properties.address;
  }
}

export type FeaturesOfInterest = {
  vehicle?: FeatureCollection;
  stop?: FeatureCollection;
};

export abstract class MapTripService {
  readonly vehicle: VehicleTracking | null = null;

  readonly tripKey: string | null = null;

  readonly origin: Stop | null = null;

  readonly destination: Stop | null = null;

  get stop(): Stop | null {
    return null;
  }

  get waitingForOthersAt(): TripServiceStop | null {
    return null;
  }

  getEtas(): TripServiceEtas | null {
    return null;
  }

  /** Collection de points d'intérêt sur lesquelles la carte devrait se placer par défaut. */
  getFeaturesOfInterest(): FeaturesOfInterest {
    return {};
  }
}

/** Sentinelle qui représente une erreur lors de la récupération du service. */
export class UnavailableMapTripService extends MapTripService {
  static readonly Instance: MapTripService = new UnavailableMapTripService();
}

/** Sentinelle qui représente l'absence de service dans un avenir proche. */
export class NoneMapTripService extends MapTripService {
  static readonly Instance: MapTripService = new NoneMapTripService();
}

export abstract class SomeMapTripService extends MapTripService {
  protected constructor(public readonly tripKey: string, public readonly pickUpTimeSlot: Interval<Dayjs>) {
    super();
  }
}

/** Un service est planifié dans un avenir proche, mais il est trop tôt pour afficher les informations en temps réel. */
export class PlannedMapTripService extends SomeMapTripService {
  constructor(tripKey: string, pickUpTimeSlot: Interval<Dayjs>, public readonly realTimeAvailableAt: Dayjs) {
    super(tripKey, pickUpTimeSlot);
  }
}

export abstract class RealTimeMapTripService extends SomeMapTripService {
  protected constructor(
    tripKey: string,
    pickUpTimeSlot: Interval<Dayjs>,
    public readonly estimatedPickUpAt: Dayjs,
    public readonly estimatedDropOffAt: Dayjs,
  ) {
    super(tripKey, pickUpTimeSlot);
  }

  getOriginEta(): TripServiceEta<'origin'> {
    return {
      stop: 'origin',
      time: this.estimatedPickUpAt,
      isStale: this.isStale,
      isNext: this.nextStop === 'origin',
    };
  }

  getDestinationEta(): TripServiceEta<'destination'> {
    return {
      stop: 'destination',
      time: this.estimatedDropOffAt,
      isStale: this.isStale,
      isNext: this.nextStop === 'destination',
    };
  }

  override getEtas(): TripServiceEtas {
    return {
      origin: this.getOriginEta(),
      destination: this.getDestinationEta(),
    };
  }

  protected abstract get nextStop(): TripServiceStop;

  protected get isStale(): boolean {
    return false;
  }
}

/** Service où le temps réel est disponible, mais il est encore trop tôt pour repérer le véhicule. */
export class EarlyMapTripService extends RealTimeMapTripService {
  constructor(
    tripKey: string,
    pickUpTimeSlot: Interval<Dayjs>,
    estimatedPickUpAt: Dayjs,
    estimatedDropOffAt: Dayjs,
    public readonly trackingAvailableAt: Dayjs,
    public override readonly origin: Stop,
  ) {
    super(tripKey, pickUpTimeSlot, estimatedPickUpAt, estimatedDropOffAt);
    this.origin = origin;
  }

  override getFeaturesOfInterest(): FeaturesOfInterest {
    return { stop: this.origin.geoJson };
  }

  protected override get nextStop(): TripServiceStop {
    return 'origin';
  }
}

/** L'embarquement est imminent ou en cours. La position du véhicule est disponible. */
export class PickUpMapTripService extends RealTimeMapTripService {
  constructor(
    tripKey: string,
    pickUpTimeSlot: Interval<Dayjs>,
    estimatedPickUpAt: Dayjs,
    estimatedDropOffAt: Dayjs,
    public readonly step: PickUpMapTripServiceStep,
    public override readonly origin: Stop,
    public override readonly vehicle: VehicleTracking,
  ) {
    super(tripKey, pickUpTimeSlot, estimatedPickUpAt, estimatedDropOffAt);
    this.vehicle = vehicle;
    this.origin = origin;
  }

  override get waitingForOthersAt(): TripServiceStop | null {
    return this.step === 'waitingOnOtherStops' ? 'origin' : null;
  }

  override getFeaturesOfInterest(): FeaturesOfInterest {
    return { vehicle: this.vehicle.geoJson, stop: this.origin.geoJson };
  }

  protected override get nextStop(): TripServiceStop {
    return 'origin';
  }
}

/** L'utilisateur se trouve dans le véhicule ou est en train de débarquer. */
export class DropOffMapTripService extends RealTimeMapTripService {
  constructor(
    tripKey: string,
    pickUpTimeSlot: Interval<Dayjs>,
    estimatedPickUpAt: Dayjs,
    estimatedDropOffAt: Dayjs,
    public readonly step: DropOffMapTripServiceStep,
    public override readonly destination: Stop,
    public override readonly vehicle: VehicleTracking,
  ) {
    super(tripKey, pickUpTimeSlot, estimatedPickUpAt, estimatedDropOffAt);
    this.vehicle = vehicle;
    this.destination = destination;
  }

  override get waitingForOthersAt(): TripServiceStop | null {
    return this.step === 'waitingOnOtherStops' ? 'destination' : null;
  }

  override getFeaturesOfInterest(): FeaturesOfInterest {
    return { vehicle: this.vehicle.geoJson, stop: this.destination.geoJson };
  }

  protected override get nextStop(): TripServiceStop {
    return 'destination';
  }
}

/**
 * Le déplacement est imminent et le repérage devrait être disponible, mais une erreur empêche de localiser
 * le véhicule.
 * */
export class PickUpTrackingErrorMapTripService extends RealTimeMapTripService {
  constructor(
    tripKey: string,
    pickUpTimeSlot: Interval<Dayjs>,
    estimatedPickUpAt: Dayjs,
    estimatedDropOffAt: Dayjs,
    public readonly step: TrackingErrorTripServiceStep,
    public override readonly origin: Stop,
  ) {
    super(tripKey, pickUpTimeSlot, estimatedPickUpAt, estimatedDropOffAt);
    this.origin = origin;
  }

  override get isStale(): boolean {
    return true;
  }

  override getFeaturesOfInterest(): FeaturesOfInterest {
    return { stop: this.origin.geoJson };
  }

  protected override get nextStop(): TripServiceStop {
    return 'origin';
  }
}

/**
 * Le déplacement est commencé et le repérage devrait être disponible, mais une erreur empêche de localiser
 * le véhicule.
 * */
export class DropOffTrackingErrorMapTripService extends RealTimeMapTripService {
  constructor(
    tripKey: string,
    pickUpTimeSlot: Interval<Dayjs>,
    estimatedPickUpAt: Dayjs,
    estimatedDropOffAt: Dayjs,
    public readonly step: TrackingErrorTripServiceStep,
    public override readonly destination: Stop,
  ) {
    super(tripKey, pickUpTimeSlot, estimatedPickUpAt, estimatedDropOffAt);
    this.destination = destination;
  }

  override get isStale(): boolean {
    return true;
  }

  override getFeaturesOfInterest(): FeaturesOfInterest {
    return { stop: this.destination.geoJson };
  }

  protected override get nextStop(): TripServiceStop {
    return 'destination';
  }
}
